/**
 * Skeleton Primitives — butterUI
 *
 * Composable loading placeholders for common layout patterns.
 * Built on the base Skeleton component from shadcn/ui.
 *
 * Page-specific composed skeletons (e.g. ProfileTabSkeleton) live
 * alongside their consuming page code, importing these primitives.
 *
 * @example
 * ```tsx
 * <SectionSkeleton showSubtitle>
 *   <div className="grid grid-cols-2 gap-4">
 *     <FieldPairSkeleton />
 *     <FieldPairSkeleton />
 *   </div>
 * </SectionSkeleton>
 * ```
 */

import { Skeleton } from '#primitives/feedback'

// ─── Field Pair (label + value) ──────────────────────────────────────

/** Two-line skeleton: small label + larger value. Used in 2-col info grids. */
export function FieldPairSkeleton() {
  return (
    <div className="space-y-1.5">
      <Skeleton className="h-3 w-16" />
      <Skeleton className="h-4 w-28" />
    </div>
  )
}

// ─── Section (header + optional subtitle + content) ──────────────────

interface SectionSkeletonProps {
  /** Show a subtitle line below the title */
  showSubtitle?: boolean
  /** Content area — pass child skeletons or leave empty for a default card */
  children?: React.ReactNode
}

/** Section block: title line + optional subtitle + children content area. */
export function SectionSkeleton({ showSubtitle, children }: SectionSkeletonProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Skeleton className="h-5 w-36" />
        {showSubtitle && <Skeleton className="h-3 w-52" />}
      </div>
      {children ?? <CardSkeleton />}
    </div>
  )
}

// ─── Card (empty state placeholder) ──────────────────────────────────

/** Bordered card with a centered text line. Matches membership/passes empty states. */
export function CardSkeleton() {
  return (
    <div className="rounded-lg border border-gray-200 p-6 flex justify-center">
      <Skeleton className="h-4 w-40" />
    </div>
  )
}

// ─── Booking Card ────────────────────────────────────────────────────

/** Booking card skeleton — matches the booking card layout */
export function BookingCardSkeleton() {
  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-5 w-20" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-4" />
        <Skeleton className="h-4 w-28" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-4" />
        <Skeleton className="h-4 w-36" />
      </div>
    </div>
  )
}
